import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Modal } from "@/shared/ui/modal";

import type { Connection } from "../lib/types";

interface DisconnectDialogProps {
  connection: Connection | null;
  marketplaceLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

/**
 * D7: the operator sees the live numbers before they stop the feed — how many
 * orders are still in flight and how many rules point at this shop — and is
 * told plainly that orders placed after this point never reach the warehouse.
 */
export function DisconnectDialog({
  connection,
  marketplaceLabel,
  onConfirm,
  onCancel,
  isPending,
}: DisconnectDialogProps): JSX.Element | null {
  const { t } = useTranslation("marketplace_connections");
  if (!connection) return null;
  const shop = connection.shop_name || connection.shop_id || "";

  return (
    <Modal
      open
      onClose={onCancel}
      title={t("disconnect.title", { shop })}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="disconnect-cancel">
            {t("disconnect.cancel")}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={isPending}
            onClick={onConfirm}
            data-testid="disconnect-confirm"
          >
            {t("disconnect.confirm")}
          </Button>
        </>
      }
    >
      <div className="space-y-2 text-sm" data-testid="disconnect-dialog" role="alert">
        <p>{t("disconnect.warning", { shop, marketplace: marketplaceLabel })}</p>
        <p className="text-[var(--text-secondary)]">
          {t("disconnect.counts", {
            openOrders: connection.open_orders ?? 0,
            activePromotions: connection.active_promotions ?? 0,
          })}
        </p>
      </div>
    </Modal>
  );
}
